import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ListMusic, Loader, Music2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CreatePlaylistDialog } from "../components/CreatePlaylistDialog";
import { EmptyState } from "../components/EmptyState";
import { MediaGrid } from "../components/MediaGrid";
import { PlaylistManager } from "../components/PlaylistManager";
import { ProviderTabs } from "../components/ProviderTabs";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { useLocalRadioStore } from "../stores/LocalRadioStore"; // Added
import { usePlaylistStore } from "../stores/playlistStore";

export const Playlists = () => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { isAuthenticated, login } = useSpotifyAuth();
  const { playlists, deletePlaylist } = usePlaylistStore();
  const { isContentLiked, toggleLike, initialized } = useLocalLibraryStore();
  const { radioStations } = useLocalRadioStore(); // Added if needed

  const { data: spotifyPlaylists, isLoading: isSpotifyLoading } = useQuery({
    queryKey: ["playlists"],
    queryFn: () => spotifyApi.currentUser.playlists.playlists(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  useEffect(() => {
    if (!initialized) {
      const checkInitialization = setInterval(() => {
        if (initialized) {
          setIsLoading(false);
          clearInterval(checkInitialization);
        }
      }, 100);

      return () => clearInterval(checkInitialization);
    } else {
      setIsLoading(false);
    }
  }, [initialized]);

  const handleDelete = () => {
    if (selectedPlaylist) {
      deletePlaylist(selectedPlaylist);
      setSelectedPlaylist(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-yellow-500" />
          <p className="text-zinc-400">Loading playlists...</p>
        </motion.div>
      </div>
    );
  }

  const renderContent = () => {
    if (activeProvider === "spotify") {
      if (!isAuthenticated) {
        return (
          <EmptyState
            icon={ListMusic}
            title="Connect to Spotify"
            description="Connect your Spotify account to access your playlists"
            action={{
              label: "Connect Spotify",
              onClick: login,
            }}
          />
        );
      }

      if (isSpotifyLoading) {
        return (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          </div>
        );
      }

      if (!spotifyPlaylists?.items?.length) {
        return (
          <EmptyState
            icon={Music2}
            title="No playlists found"
            description="Create some playlists to see them here"
            action={{
              label: "Create Playlist",
              onClick: () => setIsCreateDialogOpen(true),
            }}
          />
        );
      }

      return (
        <MediaGrid
          items={spotifyPlaylists.items.map((playlist) => ({
            id: playlist.id,
            name: playlist.name,
            artist: playlist.owner.display_name || "Unknown",
            coverUrl: playlist.images[0]?.url,
            trackCount: playlist.tracks?.total ?? 0,
            source: "spotify" as const,
            type: "playlist" as const,
            isLiked: isContentLiked(playlist.id, "playlist"),
            onLike: () => toggleLike(playlist.id, "playlist", "spotify"),
          }))}
        />
      );
    }

    if (!playlists.length) {
      return (
        <EmptyState
          icon={Music2}
          title="Create your first playlist"
          description="Start organizing your music into playlists"
          action={{
            label: "Create Playlist",
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      );
    }

    return (
      <MediaGrid
        items={playlists.map((playlist) => ({
          id: playlist.id,
          name: playlist.name,
          artist: "Your Playlist",
          coverUrl: playlist.icon,
          trackCount: playlist.tracks.length,
          source: "local" as const,
          type: "playlist" as const,
          isLiked: isContentLiked(playlist.id, "playlist"),
          onLike: () => toggleLike(playlist.id, "playlist", "local"),
          onEdit: () => {
            setSelectedPlaylist(playlist.id);
            setShowEditDialog(true);
          },
          onDelete: () => {
            setSelectedPlaylist(playlist.id);
            setShowDeleteConfirm(true);
          },
        }))}
      />
    );
  };

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Your Playlists</h1>
            <p className="text-zinc-400">
              {activeProvider === "spotify" ? spotifyPlaylists?.items?.length || 0 : playlists.length} playlist
              {(activeProvider === "spotify" ? spotifyPlaylists?.items?.length : playlists.length) !== 1 ? "s" : ""}
            </p>
          </div>

          <button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition"
          >
            <Plus className="w-5 h-5" />
            Create Playlist
          </button>
        </div>

        <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

        {renderContent()}

        <CreatePlaylistDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          provider={activeProvider}
        />

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedPlaylist(null);
          }}
          onConfirm={handleDelete}
          title="Delete Playlist"
          message="Are you sure you want to delete this playlist? This action cannot be undone."
          confirmLabel="Delete"
          type="danger"
        />

        {showEditDialog && selectedPlaylist && (
          <PlaylistManager
            playlistId={selectedPlaylist}
            isOpen={true}
            onClose={() => {
              setShowEditDialog(false);
              setSelectedPlaylist(null);
            }}
          />
        )}
      </div>
    </div>
  );
};
